/**
 * ChallengeProgressGrid - Visual Progress Grid for Envelope Challenge
 * Displays all challenge days with color-coded status:
 * - Green: Completed (saved)
 * - Yellow: Vaulted/Skipped
 * - Grey: Pending (future)
 * - Highlighted: Today
 * 
 * Adapts sizing based on total days:
 * - ≤30 days: Mini envelope icons
 * - >30 days: Compact dots/squares (GitHub contribution graph style)
 */
import { motion } from 'framer-motion';
import { Mail, Check, Clock, Pause } from 'lucide-react';

export default function ChallengeProgressGrid({ 
  envelopes = [], 
  totalDays = 30, 
  currentDay = 1,
  className = '' 
}) {
  // Determine display mode based on total days
  const isCompactMode = totalDays > 30;
  
  // Generate array of all days with their status
  const getDayStatus = (dayNumber) => {
    const envelope = envelopes.find(e => e.day === dayNumber || e.id === dayNumber);
    
    if (envelope) {
      if (envelope.status === 'saved' || envelope.status === 'completed') {
        return 'completed';
      }
      if (envelope.status === 'skipped' || envelope.status === 'vaulted') {
        return 'vaulted';
      }
      if (envelope.status === 'revealed' || envelope.status === 'pending') {
        return dayNumber === currentDay ? 'today' : 'pending';
      }
    }
    
    if (dayNumber === currentDay) {
      return 'today';
    }
    
    if (dayNumber < currentDay) {
      // Past day without explicit status - likely missed
      return 'pending';
    }
    
    return 'future';
  };

  // Status colors and styles
  const getStatusStyles = (status) => {
    switch (status) {
      case 'completed':
        return {
          bg: 'bg-emerald-500',
          border: 'border-emerald-400',
          glow: 'shadow-emerald-500/30',
          icon: Check,
          iconColor: 'text-white'
        };
      case 'vaulted':
        return {
          bg: 'bg-amber-400',
          border: 'border-amber-300',
          glow: 'shadow-amber-400/30',
          icon: Pause,
          iconColor: 'text-amber-900'
        };
      case 'today':
        return {
          bg: 'bg-gradient-to-br from-blue-500 to-purple-600',
          border: 'border-blue-400',
          glow: 'shadow-blue-500/50 shadow-lg',
          icon: Mail,
          iconColor: 'text-white',
          pulse: true
        };
      case 'pending':
        return {
          bg: 'bg-gray-600',
          border: 'border-gray-500',
          glow: '',
          icon: Clock,
          iconColor: 'text-gray-400'
        };
      case 'future':
      default:
        return {
          bg: 'bg-gray-700/50',
          border: 'border-gray-600/50',
          glow: '',
          icon: null,
          iconColor: 'text-gray-500'
        };
    }
  };

  // Generate all days
  const days = Array.from({ length: totalDays }, (_, i) => ({
    day: i + 1,
    status: getDayStatus(i + 1)
  }));

  // Calculate grid columns based on total days
  const getGridCols = () => {
    if (totalDays <= 7) return 'grid-cols-7';
    if (totalDays <= 14) return 'grid-cols-7';
    if (totalDays <= 30) return 'grid-cols-6 sm:grid-cols-10';
    if (totalDays <= 60) return 'grid-cols-10 sm:grid-cols-15';
    return 'grid-cols-14 sm:grid-cols-20'; // For 100+ days
  };

  // Calculate completion stats
  const completedCount = days.filter(d => d.status === 'completed').length;
  const vaultedCount = days.filter(d => d.status === 'vaulted').length;
  const completionPercentage = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;

  return (
    <div className={`bg-slate-800/50 rounded-2xl p-4 ${className}`}>
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <span>📊</span> Challenge Progress
        </h3>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-400">Day {currentDay} of {totalDays}</span>
          <span className="text-emerald-400 font-bold">{completionPercentage}%</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span className="text-gray-400">Saved ({completedCount})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber-400" />
          <span className="text-gray-400">Vaulted ({vaultedCount})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gradient-to-br from-blue-500 to-purple-600" />
          <span className="text-gray-400">Today</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-600" />
          <span className="text-gray-400">Pending</span>
        </div>
      </div>

      {/* Scrollable Grid Container */}
      <div 
        className="max-h-[250px] overflow-y-auto pr-2 scrollbar-thin scrollbar-track-slate-700 scrollbar-thumb-slate-500"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#64748b #334155'
        }}
      >
        {isCompactMode ? (
          /* Compact Mode - Small dots/squares for >30 days */
          <div 
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${totalDays > 100 ? '16px' : '20px'}, 1fr))`
            }}
          >
            {days.map((dayData, index) => {
              const styles = getStatusStyles(dayData.status);
              return (
                <motion.div
                  key={dayData.day}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.002, duration: 0.2 }}
                  className={`
                    aspect-square rounded-sm cursor-pointer
                    ${styles.bg} ${styles.glow}
                    ${styles.pulse ? 'animate-pulse' : ''}
                    hover:scale-125 hover:z-10 transition-transform
                    relative group
                  `}
                  title={`Day ${dayData.day}: ${dayData.status}`}
                >
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                    Day {dayData.day}
                    <span className="ml-1 capitalize">({dayData.status})</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* Standard Mode - Mini envelopes for ≤30 days */
          <div 
            className={`grid gap-2 ${getGridCols()}`}
          >
            {days.map((dayData, index) => {
              const styles = getStatusStyles(dayData.status);
              const IconComponent = styles.icon;
              
              return (
                <motion.div
                  key={dayData.day}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02, duration: 0.3 }}
                  className={`
                    aspect-square rounded-lg p-1 flex flex-col items-center justify-center
                    border ${styles.border} ${styles.bg} ${styles.glow}
                    ${styles.pulse ? 'animate-pulse' : ''}
                    cursor-pointer hover:scale-105 transition-transform
                    relative group
                  `}
                >
                  {/* Day number */}
                  <span className={`text-[10px] font-bold ${
                    dayData.status === 'future' ? 'text-gray-500' : 'text-white/80'
                  }`}>
                    {dayData.day}
                  </span>
                  
                  {/* Icon */}
                  {IconComponent && (
                    <IconComponent className={`w-3 h-3 ${styles.iconColor}`} />
                  )}
                  
                  {/* Mini envelope for today */}
                  {dayData.status === 'today' && (
                    <span className="text-xs">📨</span>
                  )}
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                    Day {dayData.day}
                    <span className="ml-1 capitalize">({dayData.status})</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mt-4 pt-4 border-t border-slate-700">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>{completedCount} of {totalDays} days completed</span>
          <span>{totalDays - currentDay} days remaining</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
    </div>
  );
}
